import {
  faLocationDot,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dropdown } from '~/components/Form'
import { AddressInput } from '~/components/Form/Input/AddressInput'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { Label } from '~/components/Form/Input/Label'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { formatAddress } from '~/domains/common/utils'
import { buildRadiusOptions } from '~/domains/taxonomies/types'
import type { AddressResponse } from '~/types/global'
import {
  locationAddress,
  locationAddressChoices,
  locationAddressDetail,
  locationAddressRadius,
  locationAddressWarningIcon
} from './MissionAddressField.css'

type MissionAddressFieldProps = InputInFormProps & {
  contactAddress?: AddressResponse | null
  addressFieldName?: string
  locationRadiusFieldName?: string
  name?: string // Required for the form to pass InputInFormProps
}

export const MissionAddressField = ({
  contactAddress,
  addressFieldName,
  locationRadiusFieldName,
  register,
  watch,
  setValue
}: MissionAddressFieldProps) => {
  if (!watch || !setValue || !addressFieldName || !locationRadiusFieldName) {
    throw new Error('addressFieldName and locationRadiusFieldName are required')
  }
  const useContactAddress = watch(addressFieldName) === null
  const address = watch(addressFieldName) as AddressResponse | null
  const hasGeocode =
    contactAddress?.lat !== null &&
    contactAddress?.lat !== undefined &&
    contactAddress?.lng !== null &&
    contactAddress?.lng !== undefined

  return (
    <div>
      <Label>Adresse de la mission</Label>
      <div className={locationAddress}>
        <div className={locationAddressChoices}>
          <Toggle
            defaultState={useContactAddress}
            label="Adresse du contact"
            onChange={(checked) => {
              if (checked) {
                setValue(addressFieldName, null, {
                  shouldDirty: true,
                  shouldValidate: true
                })
              } else {
                setValue(addressFieldName, '')
              }
            }}
          />
          <div className={locationAddressRadius}>
            Rayon de recherche
            <Dropdown
              small
              required
              register={register}
              watch={watch}
              setValue={setValue}
              name={locationRadiusFieldName}
              options={buildRadiusOptions('full').map((r) => ({
                label: r.name,
                value: r.id.toString()
              }))}
            />
          </div>
        </div>

        {useContactAddress && contactAddress && (
          <div className={locationAddressDetail}>
            {!hasGeocode && (
              <Tooltip
                text="Cette adresse n'est pas géolocalisée. Le matching par proximité ne fonctionnera pas avec cette adresse."
                direction="top"
              >
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className={locationAddressWarningIcon}
                  title="Cette adresse n'est pas géolocalisée. Le matching par proximité ne fonctionnera pas avec cette adresse."
                />
              </Tooltip>
            )}
            <FontAwesomeIcon icon={faLocationDot} />
            {formatAddress(contactAddress)}
          </div>
        )}

        {!useContactAddress && (
          <AddressInput
            placeholder="Saisissez une adresse"
            defaultValue={address}
            onChange={(nextAddress) => {
              setValue(addressFieldName, nextAddress, {
                shouldDirty: true,
                shouldValidate: true
              })
            }}
          />
        )}
      </div>
    </div>
  )
}
